import { z } from 'zod';
import { APPROVAL_DECIDED_VIA, APPROVAL_DECISIONS } from './approvals';
import { CommentVisibilitySchema } from './comments';
import { JobStatusSchema } from './jobs';
import { NOTIFICATION_CHANNELS } from './notifications';
import { SLA_KINDS } from './sla';
import { TIMER_KINDS } from './timers';
import { ApprovalModeSchema, StatusCategorySchema } from './workflow/rules';

/**
 * Registry of domain event types. Workers subscribe by type via EventBridge rules.
 * Keep in sync with aidlc/domain/events.md.
 */
export const EVENT_TYPES = [
  'tenant.created',
  'user.invited',
  'membership.changed',
  'project.created',
  'project.config_changed',
  'ticket.created',
  'ticket.updated',
  'ticket.transitioned',
  'ticket.acknowledged',
  'ticket.assigned',
  'ticket.moved',
  'ticket.deleted',
  'comment.added',
  'comment.updated',
  'comment.deleted',
  'attachment.added',
  'approval.requested',
  'approval.decided',
  'sla.breached',
  'automation.executed',
  // Bolt 8: time-based trigger firing (timers worker) and delivery logs (workers).
  'automation.due',
  'notification.requested',
  'notification.sent',
  'webhook.delivered',
  'webhook.dead',
  'job.queued',
  'job.completed',
  // Bolt 9 (S-09-05, S-09-09): the only event an import batch emits; closes a completed or
  // partially-completed run. `ticket.*`/`comment.*`/`attachment.*` are never emitted for
  // import-created rows (ticket_event history only) — the volume argument from events.md.
  'import.completed',
] as const;
export const EventTypeSchema = z.enum(EVENT_TYPES);
export type EventType = z.infer<typeof EventTypeSchema>;

export const ACTOR_TYPES = ['user', 'system', 'api_key', 'automation', 'importer'] as const;
export const ActorTypeSchema = z.enum(ACTOR_TYPES);
export type ActorType = z.infer<typeof ActorTypeSchema>;

export const ActorSchema = z.object({
  type: ActorTypeSchema,
  id: z.string().nullable(),
});
export type Actor = z.infer<typeof ActorSchema>;

export const AggregateRefSchema = z.object({
  type: z.string().min(1),
  id: z.uuid(),
  version: z.int().min(1).nullable(),
});

/** Envelope written to `outbox_event` in the same transaction as the mutation. */
export const DomainEventSchema = z.object({
  id: z.uuid(),
  type: EventTypeSchema,
  schemaVersion: z.literal(1),
  occurredAt: z.iso.datetime(),
  tenantId: z.uuid(),
  projectId: z.uuid().nullable(),
  actor: ActorSchema,
  aggregate: AggregateRefSchema,
  data: z.record(z.string(), z.unknown()),
  correlationId: z.string().nullable(),
  /** Id of the event that caused this one (automation chains). */
  causationId: z.uuid().nullable(),
  /** Automation chain depth; rules do not fire at depth >= MAX_EVENT_DEPTH. */
  depth: z.int().min(0),
});
export type DomainEvent = z.infer<typeof DomainEventSchema>;

export const MAX_EVENT_DEPTH = 5;

/** Ticket-level change record shared by history and events. */
export const FieldChangeSchema = z.object({
  field: z.string(),
  from: z.unknown().optional(),
  to: z.unknown().optional(),
  fromDisplay: z.string().nullable().optional(),
  toDisplay: z.string().nullable().optional(),
});
export type FieldChange = z.infer<typeof FieldChangeSchema>;

/* ----------------------------- payloads (Bolt 5) ----------------------------- */

/**
 * `data` payloads per type (aidlc/domain/events.md): ids not labels, small, no PII beyond
 * ids. Importer batches add `import: { batchId }`; the notifications and automations
 * workers skip those.
 */
export const ImportMarkerSchema = z.object({ batchId: z.uuid() });

export const TicketCreatedDataSchema = z.object({
  number: z.int().min(1),
  key: z.string(),
  issueTypeId: z.uuid(),
  statusId: z.uuid(),
  priorityId: z.uuid().nullable(),
  reporterId: z.uuid(),
  assigneeId: z.uuid().nullable(),
  organizationId: z.uuid().nullable(),
  parentId: z.uuid().nullable().optional(),
  labels: z.array(z.string()).optional(),
  /** Custom values by `cf_` key (ids). */
  fields: z.record(z.string(), z.unknown()),
  import: ImportMarkerSchema.optional(),
});
export type TicketCreatedData = z.infer<typeof TicketCreatedDataSchema>;

/** Summary / description / labels / priority / custom fields changed; never status. */
export const TicketUpdatedDataSchema = z.object({
  changes: z.array(FieldChangeSchema).min(1),
  import: ImportMarkerSchema.optional(),
});
export type TicketUpdatedData = z.infer<typeof TicketUpdatedDataSchema>;

/** Transition, admin move (`synthetic: true`, no `transitionId`) or status migration. */
export const TicketTransitionedDataSchema = z.object({
  transitionId: z.uuid().nullable(),
  fromStatusId: z.uuid(),
  toStatusId: z.uuid(),
  toCategory: StatusCategorySchema,
  synthetic: z.boolean().optional(),
  import: ImportMarkerSchema.optional(),
});
export type TicketTransitionedData = z.infer<typeof TicketTransitionedDataSchema>;

export const TicketAssignedDataSchema = z.object({
  from: z.uuid().nullable(),
  to: z.uuid().nullable(),
  /** Set when an `assign` post-function / action chose the assignee. */
  strategy: z.string().max(40).optional(),
});
export type TicketAssignedData = z.infer<typeof TicketAssignedDataSchema>;

export const TicketAcknowledgedDataSchema = z.object({
  acknowledgedBy: z.uuid(),
  acknowledgedAt: z.iso.datetime(),
});
export type TicketAcknowledgedData = z.infer<typeof TicketAcknowledgedDataSchema>;

export const TicketMovedDataSchema = z.object({
  fromProjectId: z.uuid(),
  toProjectId: z.uuid(),
  fromKey: z.string(),
  toKey: z.string(),
  issueTypeId: z.uuid(),
  statusId: z.uuid(),
  /** `cf_` keys whose values went to `external_ref.unmapped`. */
  unmapped: z.array(z.string()),
});
export type TicketMovedData = z.infer<typeof TicketMovedDataSchema>;

export const TicketDeletedDataSchema = z.object({
  key: z.string(),
  number: z.int().min(1),
});
export type TicketDeletedData = z.infer<typeof TicketDeletedDataSchema>;

export const CommentAddedDataSchema = z.object({
  commentId: z.uuid(),
  ticketId: z.uuid(),
  authorId: z.uuid(),
  visibility: CommentVisibilitySchema,
  mentions: z.array(z.uuid()),
  import: ImportMarkerSchema.optional(),
});
export type CommentAddedData = z.infer<typeof CommentAddedDataSchema>;

export const CommentChangedDataSchema = z.object({
  commentId: z.uuid(),
  ticketId: z.uuid(),
  authorId: z.uuid(),
  visibility: CommentVisibilitySchema,
});
export type CommentChangedData = z.infer<typeof CommentChangedDataSchema>;

/** Emitted when the upload is verified (`status: 'ready'`). */
export const AttachmentAddedDataSchema = z.object({
  attachmentId: z.uuid(),
  ticketId: z.uuid(),
  commentId: z.uuid().nullable(),
  filename: z.string(),
  contentType: z.string().optional(),
  sizeBytes: z.int().min(0),
});
export type AttachmentAddedData = z.infer<typeof AttachmentAddedDataSchema>;

/** Feeds the jobs worker (EventBridge rule `job.queued` → SQS). */
export const JobQueuedDataSchema = z.object({
  jobId: z.uuid(),
  kind: z.string().min(1).max(80),
});
export type JobQueuedData = z.infer<typeof JobQueuedDataSchema>;

export const JobCompletedDataSchema = z.object({
  jobId: z.uuid(),
  kind: z.string().min(1).max(80),
  status: JobStatusSchema,
});
export type JobCompletedData = z.infer<typeof JobCompletedDataSchema>;

/* ----------------------------- payloads (Bolt 8) ----------------------------- */

/**
 * Outcome of one rule × event evaluation (`automation_run.status`). Lives here rather than in
 * `automation.ts` because the `automation.executed` payload needs it and `automation.ts`
 * imports this module (trigger event types).
 */
export const AUTOMATION_RUN_STATUSES = [
  'succeeded',
  'failed',
  'skipped_conditions',
  'skipped_depth',
  'skipped_suppressed',
  'dry_run',
] as const;
export const AutomationRunStatusSchema = z.enum(AUTOMATION_RUN_STATUSES);
export type AutomationRunStatus = z.infer<typeof AutomationRunStatusSchema>;

/** `approval_required` created the pending approvals of one request (S-07-09). */
export const ApprovalRequestedDataSchema = z.object({
  requestId: z.uuid(),
  transitionId: z.uuid(),
  mode: ApprovalModeSchema,
  approvalIds: z.array(z.uuid()).min(1),
  approverIds: z.array(z.uuid()).min(1),
  /** Who executed the transition that raised the request; null for automations. */
  requestedBy: z.uuid().nullable(),
});
export type ApprovalRequestedData = z.infer<typeof ApprovalRequestedDataSchema>;

export const ApprovalDecidedDataSchema = z.object({
  approvalId: z.uuid(),
  requestId: z.uuid(),
  transitionId: z.uuid(),
  approverId: z.uuid(),
  decision: z.enum(APPROVAL_DECISIONS).exclude(['pending']),
  comment: z.string().nullable(),
  decidedVia: z.enum(APPROVAL_DECIDED_VIA),
  /** State of the whole request after this decision, with `mode` applied. */
  requestOutcome: z.enum(APPROVAL_DECISIONS),
});
export type ApprovalDecidedData = z.infer<typeof ApprovalDecidedDataSchema>;

/** The timers worker found a running `ticket_sla` past its due time (S-07-08). */
export const SlaBreachedDataSchema = z.object({
  ticketSlaId: z.uuid(),
  policyId: z.uuid(),
  kind: z.enum(SLA_KINDS),
  dueAt: z.iso.datetime(),
  priorityId: z.uuid().nullable(),
});
export type SlaBreachedData = z.infer<typeof SlaBreachedDataSchema>;

/** One rule ran, whatever the outcome (audit). Rules never trigger on this type. */
export const AutomationExecutedDataSchema = z.object({
  ruleId: z.uuid(),
  runId: z.uuid(),
  triggerEventId: z.uuid().nullable(),
  status: AutomationRunStatusSchema,
  /** Action types applied, in rule order. */
  actionsApplied: z.array(z.string()),
  durationMs: z.int().min(0).nullable(),
});
export type AutomationExecutedData = z.infer<typeof AutomationExecutedDataSchema>;

/**
 * A time-based trigger is due (`schedule`, `date_field`, `notify` repeat): emitted by the
 * timers worker with `depth = 0` so the automations worker handles every trigger the same
 * way, idempotently through `processed_event` (aidlc/domain/events.md).
 */
export const AutomationDueDataSchema = z.object({
  ruleId: z.uuid(),
  timerId: z.uuid(),
  timerKind: z.enum(TIMER_KINDS),
  ticketId: z.uuid().nullable(),
  /** `date_field` triggers: the field the timer was armed from. */
  field: z.string().optional(),
  scheduledFor: z.iso.datetime(),
});
export type AutomationDueData = z.infer<typeof AutomationDueDataSchema>;

/**
 * Internal request for the notifications worker to deliver a notification without a domain
 * event driving it directly (`packages/jobs/src/notifications-request.ts`): the `notify`
 * automation action and the timers worker's repeats (`notify_repeat`) already resolved
 * recipients and channels, so the worker skips recipient resolution and preference filtering
 * and delivers exactly what is asked (still subject to suppression and exactly-once).
 */
export const NotificationRequestedDataSchema = z.object({
  /** Freeform label surfaced as `notification.kind` in the inbox (e.g. `automation.notify`). */
  kind: z.string().min(1).max(80),
  ticketId: z.uuid().nullable(),
  recipients: z.array(z.uuid()).min(1),
  channels: z.array(z.enum(NOTIFICATION_CHANNELS)).min(1),
  /** Message text with placeholders already resolved by the caller (the `notify` action's
   *  own `template`, or a timer repeat's copy of it); a generic message when absent. */
  template: z.string().max(2000).nullable(),
  /** The event that triggered this request, when any (used for the send-log dedupe key). */
  sourceEventId: z.uuid().nullable(),
});
export type NotificationRequestedData = z.infer<typeof NotificationRequestedDataSchema>;

/** The notifications worker delivered on one channel (send log mirror; S-08-05). */
export const NotificationSentDataSchema = z.object({
  /** In-app row id; null for email. */
  notificationId: z.uuid().nullable(),
  recipientId: z.uuid(),
  channel: z.enum(NOTIFICATION_CHANNELS),
  sourceEventId: z.uuid(),
  sourceEventType: z.string().min(1),
  providerMessageId: z.string().nullable().optional(),
});
export type NotificationSentData = z.infer<typeof NotificationSentDataSchema>;

export const WebhookDeliveredDataSchema = z.object({
  deliveryId: z.uuid(),
  subscriptionId: z.uuid(),
  /** The event that was posted to the endpoint. */
  deliveredEventId: z.uuid(),
  deliveredEventType: z.string().min(1),
  attempt: z.int().min(1),
  responseStatus: z.int().nullable(),
});
export type WebhookDeliveredData = z.infer<typeof WebhookDeliveredDataSchema>;

/** Retries exhausted (S-08-07); `subscriptionDisabled` when this hit the consecutive-failure cap. */
export const WebhookDeadDataSchema = z.object({
  deliveryId: z.uuid(),
  subscriptionId: z.uuid(),
  deliveredEventId: z.uuid(),
  deliveredEventType: z.string().min(1),
  attempts: z.int().min(1),
  error: z.string().nullable(),
  subscriptionDisabled: z.boolean(),
});
export type WebhookDeadData = z.infer<typeof WebhookDeadDataSchema>;

/** Payload schema by type for the types whose payload is fixed; consumers validate with it. */
export const EVENT_DATA_SCHEMAS: Partial<Record<EventType, z.ZodType>> = {
  'ticket.created': TicketCreatedDataSchema,
  'ticket.updated': TicketUpdatedDataSchema,
  'ticket.transitioned': TicketTransitionedDataSchema,
  'ticket.assigned': TicketAssignedDataSchema,
  'ticket.acknowledged': TicketAcknowledgedDataSchema,
  'ticket.moved': TicketMovedDataSchema,
  'ticket.deleted': TicketDeletedDataSchema,
  'comment.added': CommentAddedDataSchema,
  'comment.updated': CommentChangedDataSchema,
  'comment.deleted': CommentChangedDataSchema,
  'attachment.added': AttachmentAddedDataSchema,
  'approval.requested': ApprovalRequestedDataSchema,
  'approval.decided': ApprovalDecidedDataSchema,
  'sla.breached': SlaBreachedDataSchema,
  'automation.executed': AutomationExecutedDataSchema,
  'automation.due': AutomationDueDataSchema,
  'notification.requested': NotificationRequestedDataSchema,
  'notification.sent': NotificationSentDataSchema,
  'webhook.delivered': WebhookDeliveredDataSchema,
  'webhook.dead': WebhookDeadDataSchema,
  'job.queued': JobQueuedDataSchema,
  'job.completed': JobCompletedDataSchema,
};
